import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { toast } from "react-toastify";
import {
  getPaymentPlatformFiles,
  getPaymentPlatformOverview,
  updatePaymentPlatformModel,
} from "../api/companyApi";
import { runClaudeChatJob } from "../api/claudeApi";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#15803d",
};

const QUICK_PROMPTS = [
  "Explain the current policy and its biggest risks.",
  "Recommend safer approval and manual-review thresholds.",
  "Improve advance sizing without increasing avoidable loss risk.",
  "Assess model readiness and tell me which data is still missing.",
];

const ALLOWED_PATCH_SECTIONS = ["thresholds", "guardrails", "weights", "offer"];

export const isExplicitModelUpdateInstruction = (value) =>
  /\b(?:update|change|set|apply|save|revise|modify)\b[\s\S]{0,80}\b(?:model|policy|threshold|guardrail|weight|offer|advance|rate)\b|\b(?:apply|save)\s+(?:it|this|these|that)\b/i
    .test(String(value || ""));

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const findJsonObject = (text, markerIndex) => {
  const start = text.lastIndexOf("{", markerIndex);
  if (start < 0) return null;
  let depth = 0;
  let quoted = false;
  let escaped = false;
  for (let index = start; index < text.length; index += 1) {
    const character = text[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (character === "\\" && quoted) {
      escaped = true;
      continue;
    }
    if (character === '"') {
      quoted = !quoted;
      continue;
    }
    if (quoted) continue;
    if (character === "{") depth += 1;
    if (character === "}") depth -= 1;
    if (depth === 0) return text.slice(start, index + 1);
  }
  return null;
};

export const extractAdminMcaModelPatch = (reply) => {
  const text = String(reply || "");
  const markerIndex = text.indexOf('"modelPatch"');
  if (markerIndex < 0) return null;
  const candidate = findJsonObject(text, markerIndex);
  if (!candidate) return null;
  try {
    const parsed = JSON.parse(candidate);
    if (!isObject(parsed.modelPatch)) return null;
    const patch = {};
    ALLOWED_PATCH_SECTIONS.forEach((section) => {
      if (isObject(parsed.modelPatch[section])) {
        patch[section] = parsed.modelPatch[section];
      }
    });
    return Object.keys(patch).length ? patch : null;
  } catch (error) {
    return null;
  }
};

export const mergeAdminMcaModelPatch = (model, patch) => {
  const current = model || {};
  const proposed = patch || {};
  return {
    ...current,
    ...ALLOWED_PATCH_SECTIONS.reduce((result, section) => {
      if (!isObject(proposed[section])) return result;
      result[section] = {
        ...(isObject(current[section]) ? current[section] : {}),
        ...proposed[section],
      };
      if (section === "offer" && isObject(proposed.offer?.factorRates)) {
        result.offer.factorRates = {
          ...(isObject(current.offer?.factorRates)
            ? current.offer.factorRates
            : {}),
          ...proposed.offer.factorRates,
        };
      }
      return result;
    }, {}),
  };
};

const PatchPreview = ({ patch }) => (
  <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
    {ALLOWED_PATCH_SECTIONS.filter((section) => isObject(patch?.[section])).map(
      (section) => (
        <Box key={section}>
          <Typography
            sx={{
              color: C.muted,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            {section}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {Object.entries(patch[section]).map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                label={`${key}: ${
                  isObject(value) ? JSON.stringify(value) : String(value)
                }`}
                sx={{ bgcolor: "#fff", border: `1px solid ${C.line}` }}
              />
            ))}
          </Box>
        </Box>
      ),
    )}
  </Box>
);

export default function AdminMcaModelChat({ tenant }) {
  const tenantId = tenant?.id;
  const [overview, setOverview] = useState(null);
  const [fileCatalog, setFileCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [nextOverview, files] = await Promise.all([
        getPaymentPlatformOverview(tenantId),
        getPaymentPlatformFiles(tenantId),
      ]);
      setOverview(nextOverview);
      setFileCatalog(files?.items || []);
    } catch (error) {
      setLoadError(
        error?.response?.data?.message ||
          "The tenant model workspace could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [busy, messages, proposal]);

  const model = overview?.model || null;
  const saveModelPatch = async (patch, baseModel, { automatic = false } = {}) => {
    if (!patch || !baseModel) return null;
    setSaving(true);
    try {
      const saved = await updatePaymentPlatformModel(
        tenantId,
        mergeAdminMcaModelPatch(baseModel, patch),
      );
      setOverview((current) => ({ ...current, model: saved }));
      setProposal(null);
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: `Model version ${saved?.version || ""} was saved as a tenant-isolated draft${automatic ? " because you explicitly requested an update" : ""}. Every new company assessment will use this model version. Review and validate it in Model Lab before deployment.`,
        },
      ]);
      toast.success(`MCA model v${saved?.version || ""} saved`);
      return saved;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "The model proposal could not be saved.",
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const send = async (prompt = input) => {
    const question = String(prompt || "").trim();
    if (!question || busy || !model) return;
    const nextMessages = [...messages, { role: "user", content: question }];
    setMessages(nextMessages);
    setInput("");
    setProposal(null);
    setBusy(true);
    try {
      const response = await runClaudeChatJob({
        tenantName: tenant?.name || "Payment platform",
        entityName: `${tenant?.name || "Tenant"} MCA / loan model`,
        entity: {
          tenantId,
          paymentPlatformModelTuning: true,
          adminModelBuilder: true,
          instruction:
            "Help a CrediArc administrator build this tenant's MCA model. Load the model and every Document Center file from the server. Base recommendations only on measured tenant evidence. Clearly state limitations and validation status. If proposing changes, finish with one modelPatch JSON object. Never activate or deploy a model.",
        },
        messages: nextMessages,
        allowWeb: false,
      });
      const reply =
        response?.data?.reply ||
        response?.reply ||
        response?.data ||
        "No response was returned.";
      const content = String(reply);
      const nextProposal = extractAdminMcaModelPatch(content);
      setMessages((current) => [
        ...current,
        { role: "assistant", content },
      ]);
      setProposal(nextProposal);
      if (nextProposal && isExplicitModelUpdateInstruction(question)) {
        await saveModelPatch(nextProposal, model, { automatic: true });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "ArcAI could not answer.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 4 }}>
        <CircularProgress size={20} />
        <Typography sx={{ color: C.muted, fontSize: 13 }}>
          Loading tenant model…
        </Typography>
      </Box>
    );
  }

  if (loadError || !model) {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {loadError || "No MCA model is configured for this tenant."}
      </Alert>
    );
  }

  return (
    <Box data-testid="admin-mca-model-chat">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap", mb: 0.5 }}>
            <Chip
              size="small"
              label={`Model v${model.version || 1}`}
              color="primary"
              variant="outlined"
            />
            <Chip size="small" label={model.status || "draft"} />
            <Chip
              size="small"
              label={
                overview?.latestImport?.validation?.status ||
                model.validation?.status ||
                "not validated"
              }
              sx={{ color: "#9a6700", bgcolor: "#fff7df" }}
            />
            <Chip
              size="small"
              label="CrediArc AI"
              sx={{ color: "#5b21b6", bgcolor: "#f3e8ff" }}
            />
            <Chip
              size="small"
              label={`${fileCatalog.length} Document Center file${fileCatalog.length === 1 ? "" : "s"}`}
              sx={{ color: C.green, bgcolor: "#ecfdf5" }}
            />
          </Box>
          <Typography sx={{ color: C.muted, fontSize: 12.5 }}>
            ArcAI analyzes every Document Center file server-side. Explicit
            update instructions save a new tenant draft used by future company
            assessments; deployment remains a separate controlled step.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.8 }}>
          <Button
            size="small"
            startIcon={<RestartAltRoundedIcon />}
            onClick={() => {
              setMessages([]);
              setProposal(null);
              setInput("");
            }}
            disabled={!messages.length && !proposal}
            sx={{ textTransform: "none" }}
          >
            New chat
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={() =>
              window.open(
                `/tenant-dashboard/${tenantId}/assessments`,
                "_blank",
                "noopener,noreferrer",
              )
            }
            sx={{ textTransform: "none" }}
          >
            Open Model Lab
          </Button>
        </Box>
      </Box>

      {!messages.length && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 0.8,
            mb: 1.2,
          }}
        >
          {QUICK_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              variant="outlined"
              startIcon={<AutoAwesomeRoundedIcon />}
              onClick={() => send(prompt)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                borderColor: C.line,
                color: C.ink,
                py: 1,
              }}
            >
              {prompt}
            </Button>
          ))}
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderColor: C.line,
          borderRadius: 2.5,
          overflow: "hidden",
          bgcolor: "#f8fafc",
        }}
      >
        <Box
          ref={scrollRef}
          sx={{
            height: 350,
            overflowY: "auto",
            p: 1.5,
            overscrollBehavior: "contain",
          }}
        >
          {!messages.length && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <AutoAwesomeRoundedIcon sx={{ color: C.blue, fontSize: 30 }} />
              <Typography sx={{ color: C.ink, fontWeight: 800, mt: 0.6 }}>
                Build this tenant’s MCA model with ArcAI
              </Typography>
              <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.3 }}>
                Ask for an explanation, a data-readiness review, or a precise
                model proposal.
              </Typography>
            </Box>
          )}
          {messages.map((message, index) => (
            <Paper
              key={`${message.role}-${index}`}
              variant="outlined"
              sx={{
                p: 1.4,
                mb: 1,
                ml: message.role === "user" ? { xs: 3, md: 9 } : 0,
                mr: message.role === "assistant" ? { xs: 3, md: 9 } : 0,
                bgcolor: message.role === "user" ? "#eaf2ff" : "#fff",
                borderColor: C.line,
              }}
            >
              <Typography
                sx={{
                  color: C.muted,
                  fontSize: 10,
                  fontWeight: 900,
                  mb: 0.3,
                }}
              >
                {message.role === "user" ? "ADMIN" : "ARCAI"}
              </Typography>
              <Typography
                sx={{ color: C.ink, fontSize: 13.5, whiteSpace: "pre-wrap" }}
              >
                {message.content}
              </Typography>
            </Paper>
          ))}
          {busy && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ color: C.muted, fontSize: 12.5 }}>
                ArcAI is analyzing the tenant model and data…
              </Typography>
            </Box>
          )}
          {proposal && (
            <Alert
              severity="warning"
              sx={{ mt: 1 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={saving}
                  onClick={() => saveModelPatch(proposal, model)}
                >
                  {saving ? "Saving…" : "Apply as new draft"}
                </Button>
              }
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                Proposed model changes
              </Typography>
              <Typography sx={{ fontSize: 12.5 }}>
                Nothing changes until you apply this proposal.
              </Typography>
              <PatchPreview patch={proposal} />
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            p: 1.25,
            bgcolor: "#fff",
            borderTop: `1px solid ${C.line}`,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            disabled={busy}
            placeholder={`Ask about ${tenant?.name || "this tenant"}’s MCA model…`}
          />
          <IconButton
            aria-label="Send model question"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            sx={{
              bgcolor: C.blue,
              color: "#fff",
              "&:hover": { bgcolor: "#1559be" },
              "&.Mui-disabled": { bgcolor: "#cbd5e1" },
            }}
          >
            {busy ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              <SendRoundedIcon />
            )}
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
